import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AppInstanceInstalledEnvelope,
  AppInstancePaidPlanAutoRenewalCancelledEnvelope,
  AppInstancePaidPlanChangedEnvelope,
  AppInstancePaidPlanPurchasedEnvelope,
  AppInstancePlanConvertedToPaidEnvelope,
  AppInstancePlanReactivatedEnvelope,
  AppInstancePlanTransferredEnvelope,
  AppInstanceRemovedEnvelope,
  GetAppInstanceResponse,
  getAppInstance as universalGetAppInstance,
} from './devcenter-app-instance-v1-app-instance-app-instances.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/app-management' };

export function getAppInstance(
  httpClient: HttpClient
): GetAppInstanceSignature {
  return () =>
    universalGetAppInstance(
      // @ts-ignore
      { httpClient }
    );
}

interface GetAppInstanceSignature {
  /**
   * Retrieves data about the instance of your app that's installed on a Wix
   * site and data about the site itself. For example, to check whether the Wix user has installed a free or paid version of your app,
   * or to check which apps made by Wix are installed on the site.
   *
   * You must authenticate this method [as a Wix app](https://dev.wix.com/docs/build-apps/develop-your-app/access/authentication/authenticate-using-oauth#step-2--make-an-authenticated-api-request).
   *
   * To retrieve `site.ownerInfo` in the response, you must
   * have the __READ SITE OWNER EMAIL__ permission scope in addition to
   * __MANAGE YOUR APP__.
   */
  (): Promise<
    NonNullablePaths<
      GetAppInstanceResponse,
      | `instance.instanceId`
      | `instance.appName`
      | `instance.isFree`
      | `instance.billing.packageName`
      | `instance.billing.billingCycle`
      | `instance.billing.timeStamp`
      | `instance.billing.freeTrialInfo.status`
      | `instance.permissions`
      | `instance.availablePlans`
      | `instance.availablePlans.${number}.packageName`
      | `instance.availablePlans.${number}.source`
      | `instance.isOriginSiteTemplate`
      | `instance.copiedFromTemplate`
      | `instance.freeTrialAvailable`
      | `site.locale`
      | `site.paymentCurrency`
      | `site.multilingual.isMultiLingual`
      | `site.multilingual.supportedLanguages`
      | `site.multilingual.supportedLanguages.${number}.languageCode`
      | `site.multilingual.supportedLanguages.${number}.locale.languageCode`
      | `site.multilingual.supportedLanguages.${number}.locale.country`
      | `site.multilingual.supportedLanguages.${number}.isPrimary`
      | `site.multilingual.supportedLanguages.${number}.countryCode`
      | `site.multilingual.supportedLanguages.${number}.resolutionMethod`
      | `site.installedWixApps`
      | `site.ownerInfo.email`
      | `site.ownerInfo.emailStatus`
      | `site.siteId`,
      7
    >
  >;
}

export const onAppInstanceInstalled = EventDefinition(
  'AppInstalled',
  false,
  (event: AppInstanceInstalledEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(event)
)<AppInstanceInstalledEnvelope>();
export const onAppInstanceRemoved = EventDefinition(
  'AppRemoved',
  false,
  (event: AppInstanceRemovedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(event)
)<AppInstanceRemovedEnvelope>();
export const onAppInstancePaidPlanAutoRenewalCancelled = EventDefinition(
  'PaidPlanAutoRenewalCancelled',
  false,
  (event: AppInstancePaidPlanAutoRenewalCancelledEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'data.operationTimeStamp' }],
        },
      ])
    )
)<AppInstancePaidPlanAutoRenewalCancelledEnvelope>();
export const onAppInstancePaidPlanChanged = EventDefinition(
  'PaidPlanChanged',
  false,
  (event: AppInstancePaidPlanChangedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'data.operationTimeStamp' }],
        },
      ])
    )
)<AppInstancePaidPlanChangedEnvelope>();
export const onAppInstancePaidPlanPurchased = EventDefinition(
  'PaidPlanPurchased',
  false,
  (event: AppInstancePaidPlanPurchasedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.operationTimeStamp' },
            { path: 'data.expiresOn' },
          ],
        },
      ])
    )
)<AppInstancePaidPlanPurchasedEnvelope>();
export const onAppInstancePlanConvertedToPaid = EventDefinition(
  'PlanConvertedToPaid',
  false,
  (event: AppInstancePlanConvertedToPaidEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.operationTimeStamp' },
            { path: 'data.expiresOn' },
          ],
        },
      ])
    )
)<AppInstancePlanConvertedToPaidEnvelope>();
export const onAppInstancePlanReactivated = EventDefinition(
  'PlanReactivated',
  false,
  (event: AppInstancePlanReactivatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.operationTimeStamp' },
            { path: 'data.expiresOn' },
          ],
        },
      ])
    )
)<AppInstancePlanReactivatedEnvelope>();
export const onAppInstancePlanTransferred = EventDefinition(
  'PlanTransferred',
  false,
  (event: AppInstancePlanTransferredEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'data.operationTimeStamp' }],
        },
      ])
    )
)<AppInstancePlanTransferredEnvelope>();

export {
  AccountInfo,
  AppInstalled,
  AppInstance,
  AppInstanceInstalledEnvelope,
  AppInstancePaidPlanAutoRenewalCancelledEnvelope,
  AppInstancePaidPlanChangedEnvelope,
  AppInstancePaidPlanPurchasedEnvelope,
  AppInstancePlanConvertedToPaidEnvelope,
  AppInstancePlanReactivatedEnvelope,
  AppInstancePlanTransferredEnvelope,
  AppInstanceRemovedEnvelope,
  AppRemoved,
  AvailablePlan,
  BaseEventMetadata,
  BillingInfo,
  FreeTrialInfo,
  FreeTrialPeriod,
  FreeTrialStatus,
  GetAppInstanceByInstanceIdRequest,
  GetAppInstanceRequest,
  GetAppInstanceResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  Locale,
  MessageEnvelope,
  Multilingual,
  OwnerInfo,
  PaidPlanAutoRenewalCancelled,
  PaidPlanChanged,
  PaidPlanPurchased,
  PaymentCycle,
  PlanConvertedToPaid,
  PlanReactivated,
  PlanTransferred,
  ReactivationReason,
  ResolutionMethod,
  SiteInfo,
  SupportedLanguage,
  WebhookIdentityType,
} from './devcenter-app-instance-v1-app-instance-app-instances.universal.js';
